import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Tooltip,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import { MaterialReactTable } from 'material-react-table';

const AttributeTypes = [
  'STRING', 'INT', 'EMAIL', 'PASSWORD', 'PHONE', 'RADIO', 
  'DROPDOWN', 'ENTITY', 'CHECKBOX', 'TEXTAREA', 'BOOLEAN', 
  'FLOAT', 'DATE', 'FILE', 'TIME', 'CITY', 'PINCODE', 'CUSTOM'
];

const TabularFormView = ({ 
  leadsFormItems = [], 
  setLeadsFormItems,
  onAddAttribute,
  onRemoveAttribute,
  onCloneAttribute,
  onAttributeChange 
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [editingOptionsForRow, setEditingOptionsForRow] = useState(null);
  const [isAddingField, setIsAddingField] = useState(false);
  const [draggedRowId, setDraggedRowId] = useState(null);

  // Sync with parent data
  useEffect(() => {
    if (leadsFormItems && leadsFormItems.length > 0) {
      setTableData([...leadsFormItems]);
    }
  }, [leadsFormItems]);

  const handleEditClick = (row) => {
    if (row.original.is_static_column) {
      alert('Static columns cannot be edited');
      return;
    }
    console.log('Editing row:', row.original);
    setEditingRowId(row.original.item_id);
    setEditedData({ ...row.original });
  };

  const handleSaveClick = () => {
    console.log('Saving data:', editedData);
    
    // Validate if substatus, must have parent
    if (editedData.statusOption === 'is_sub_status' && !editedData.status_parent_key) {
      alert('Please select a parent status field for sub-status');
      return;
    }
    
    const updatedData = tableData.map(item => 
      item.item_id === editingRowId ? { ...editedData } : item
    );
    setTableData(updatedData);
    
    if (setLeadsFormItems) {
      setLeadsFormItems(updatedData);
    }
    
    if (onAttributeChange) {
      const index = tableData.findIndex(item => item.item_id === editingRowId);
      onAttributeChange(editedData, index);
    }
    
    setEditingRowId(null);
    setEditedData({});
  };

  const autoSaveChanges = (rowId, updates) => {
    const updatedData = tableData.map(item => 
      item.item_id === rowId ? { ...item, ...updates } : item
    );
    setTableData(updatedData);
    
    if (setLeadsFormItems) {
      setLeadsFormItems(updatedData);
    }
    
    if (onAttributeChange) {
      const index = tableData.findIndex(item => item.item_id === rowId);
      onAttributeChange({ ...tableData[index], ...updates }, index);
    }
  };

  const handleCancelClick = () => {
    setEditingRowId(null);
    setEditedData({});
  };

  const handleDeleteRow = (row) => {
    if (row.original.is_primary) {
      alert('Cannot delete primary key field');
      return;
    }
    if (row.original.is_static_column) {
      alert('Cannot delete static columns');
      return;
    }
    
    const updatedData = tableData.filter(item => item.item_id !== row.original.item_id);
    setTableData(updatedData);
    
    if (setLeadsFormItems) {
      setLeadsFormItems(updatedData);
    }
    
    if (onRemoveAttribute) {
      const index = tableData.findIndex(item => item.item_id === row.original.item_id);
      onRemoveAttribute(row.original, index);
    }
  };

  const handleCloneRow = (row) => {
    const maxId = Math.max(...tableData.map(f => f.item_id || 0), 0);
    const newId = maxId + 1;
    const clonedRow = { 
      ...row.original, 
      item_id: newId, 
      is_primary: false,
      is_static_column: false,
      order_id: tableData.length + 1 
    };
    
    const updatedData = [...tableData, clonedRow];
    setTableData(updatedData);
    
    if (setLeadsFormItems) {
      setLeadsFormItems(updatedData);
    }
    
    if (onCloneAttribute) {
      const index = tableData.findIndex(item => item.item_id === row.original.item_id);
      onCloneAttribute(index);
    }
  };

  const handleAddNew = () => {
    if (isAddingField) return; // Prevent double clicks
    setIsAddingField(true);
    
    const maxId = Math.max(...tableData.map(f => f.item_id || 0), 0);
    const newId = maxId + 1;
    
    const newRow = {
      item_id: newId,
      key_name: '',
      alias_name: '',
      key_type: 'STRING',
      is_required: false,
      is_primary: false,
      is_static_column: false,
      key_options: [],
      statusOption: '',
      status_parent_key: '',
      is_hot_lead: false,
      isFilter: false,
      isGroup: false,
      isSuggestion: false,
      order_id: tableData.length + 1
    };
    
    const updatedData = [...tableData, newRow];
    setTableData(updatedData);
    
    if (setLeadsFormItems) {
      setLeadsFormItems(updatedData);
    }
    
    if (onAddAttribute) {
      onAddAttribute();
    }
    
    setTimeout(() => {
      setIsAddingField(false);
    }, 300);
  };

  const exportToExcel = () => {
    const headers = ['Field Name', 'Alias Name', 'Field Type', 'Required', 'Primary', 
                     'Options', 'Status Option', 'Hot Lead', 'Filter', 'Group', 'Suggestion'];
    const rows = tableData.map(row => [
      row.key_name,
      row.alias_name,
      row.key_type,
      row.is_required,
      row.is_primary,
      Array.isArray(row.key_options) ? row.key_options.join(', ') : '',
      row.statusOption,
      row.is_hot_lead,
      row.isFilter,
      row.isGroup,
      row.isSuggestion
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'form-fields.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenOptionsModal = (row) => {
    const options = Array.isArray(row.original.key_options) ? row.original.key_options : [];
    setCurrentOptions([...options]);
    setEditingOptionsForRow(row.original);
    setShowOptionsModal(true);
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      if (currentOptions.includes(newOption.trim())) {
        alert('This option already exists');
        return;
      }
      setCurrentOptions([...currentOptions, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index) => {
    setCurrentOptions(currentOptions.filter((_, i) => i !== index));
  };

  const handleSaveOptions = () => {
    if (editingOptionsForRow) {
      const updatedData = tableData.map(item => 
        item.item_id === editingOptionsForRow.item_id 
          ? { ...item, key_options: [...currentOptions] }
          : item
      );
      setTableData(updatedData);
      
      if (setLeadsFormItems) {
        setLeadsFormItems(updatedData);
      }
      
      // If currently editing this row, update editedData too
      if (editingRowId === editingOptionsForRow.item_id) {
        setEditedData(prev => ({ ...prev, key_options: [...currentOptions] }));
      }
    }
    setShowOptionsModal(false);
    setCurrentOptions([]);
    setNewOption('');
    setEditingOptionsForRow(null);
  };

  const handleCancelOptions = () => {
    setShowOptionsModal(false);
    setCurrentOptions([]);
    setNewOption('');
    setEditingOptionsForRow(null);
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'drag',
      header: '',
      size: 40,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box
          sx={{ 
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:active': { cursor: 'grabbing' }
          }}
        >
          <Typography variant="body2" color="text.secondary">⋮⋮</Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'order_id',
      header: '#',
      size: 60,
      enableEditing: false,
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.index + 1}
        </Typography>
      ),
    },
    {
      accessorKey: 'key_name',
      header: 'Field Name',
      size: 150,
      Cell: ({ row }) => {
        const isStatic = row.original.is_static_column;
        
        return (
          <TextField
            size="small"
            fullWidth
            value={row.original.key_name ?? ''}
            onChange={(e) => {
              const newValue = e.target.value;
              const updatedData = tableData.map(item =>
                item.item_id === row.original.item_id ? { ...item, key_name: newValue } : item
              );
              setTableData(updatedData);
            }}
            onBlur={(e) => autoSaveChanges(row.original.item_id, { key_name: e.target.value })}
            disabled={row.original.is_primary || isStatic}
            placeholder="Enter field name"
          />
        );
      },
    },
    {
      accessorKey: 'alias_name',
      header: 'Alias Name',
      size: 150,
      Cell: ({ row }) => {
        const isStatic = row.original.is_static_column;
        
        return (
          <TextField
            size="small"
            fullWidth
            value={row.original.alias_name ?? ''}
            onChange={(e) => {
              const newValue = e.target.value;
              const updatedData = tableData.map(item =>
                item.item_id === row.original.item_id ? { ...item, alias_name: newValue } : item
              );
              setTableData(updatedData);
            }}
            onBlur={(e) => autoSaveChanges(row.original.item_id, { alias_name: e.target.value })}
            disabled={isStatic}
            placeholder="Enter alias name"
          />
        );
      },
    },
    {
      accessorKey: 'key_type',
      header: 'Field Type',
      size: 130,
      Cell: ({ row }) => {
        const isStatic = row.original.is_static_column;
        
        return (
          <Select
            size="small"
            fullWidth
            value={row.original.key_type ?? 'STRING'}
            onChange={(e) => {
              const newType = e.target.value;
              const updates = { key_type: newType };
              
              // Clear status options if not DROPDOWN
              if (newType !== 'DROPDOWN') {
                updates.statusOption = '';
                updates.status_parent_key = '';
              }
              
              const updatedData = tableData.map(item =>
                item.item_id === row.original.item_id ? { ...item, ...updates } : item
              );
              setTableData(updatedData);
              autoSaveChanges(row.original.item_id, updates);
            }}
            disabled={row.original.is_primary || isStatic}
          >
            {AttributeTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      accessorKey: 'key_options',
      header: 'Options',
      size: 150,
      Cell: ({ row }) => {
        const currentRow = row.original;
        const options = Array.isArray(currentRow.key_options) 
          ? currentRow.key_options 
          : [];
        const isOptionType = ['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(currentRow.key_type);
        const isStatus = currentRow.statusOption === 'is_status';
        const isSubStatus = currentRow.statusOption === 'is_sub_status';
        
        // Don't show options management for status/substatus
        if (isStatus || isSubStatus) {
          return (
            <Typography variant="caption" color="text.secondary">
              {isStatus ? 'Status Field' : 'Sub-Status Field'}
            </Typography>
          );
        }
        
        if (!isOptionType && options.length === 0) return '-';
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {options.length > 0 ? (
              <>
                <Tooltip title={options.join(', ')} arrow>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {options.slice(0, 2).map((opt, idx) => (
                      <Chip key={idx} label={opt} size="small" sx={{ fontSize: 10 }} />
                    ))}
                    {options.length > 2 && (
                      <Chip label={`+${options.length - 2}`} size="small" sx={{ fontSize: 10 }} />
                    )}
                  </Box>
                </Tooltip>
                {isOptionType && (
                  <Tooltip title="Edit Options" arrow>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenOptionsModal(row)}
                      sx={{ ml: 0.5 }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : isOptionType ? (
              <Button 
                size="small" 
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenOptionsModal(row)}
                sx={{ textTransform: 'none', fontSize: 11 }}
              >
                Add Options
              </Button>
            ) : '-'}
          </Box>
        );
      },
    },
    {
      accessorKey: 'is_required',
      header: 'Required',
      size: 100,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.is_required ?? false}
          onChange={(e) => {
            const updates = { is_required: e.target.checked };
            const updatedData = tableData.map(item =>
              item.item_id === row.original.item_id ? { ...item, ...updates } : item
            );
            setTableData(updatedData);
            autoSaveChanges(row.original.item_id, updates);
          }}
          disabled={row.original.is_primary}
        />
      ),
    },
    {
      accessorKey: 'is_primary',
      header: 'Primary',
      size: 100,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.is_primary || false}
          disabled
        />
      ),
    },
    {
      accessorKey: 'statusOption',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => {
        const isDropdown = row.original.key_type === 'DROPDOWN';
        
        if (!isDropdown) return '-';
        
        return (
          <Select
            size="small"
            fullWidth
            value={row.original.statusOption ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              const updates = { statusOption: value };
              
              // Clear parent if not substatus
              if (value !== 'is_sub_status') {
                updates.status_parent_key = '';
              }
              
              // Clear options if status or substatus
              if (value === 'is_status' || value === 'is_sub_status') {
                updates.key_options = [];
              }
              
              const updatedData = tableData.map(item =>
                item.item_id === row.original.item_id ? { ...item, ...updates } : item
              );
              setTableData(updatedData);
              autoSaveChanges(row.original.item_id, updates);
            }}
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value="is_status">Status</MenuItem>
            <MenuItem value="is_sub_status">Sub Status</MenuItem>
          </Select>
        );
      },
    },
    {
      accessorKey: 'status_parent_key',
      header: 'Parent Status',
      size: 140,
      Cell: ({ row }) => {
        const isSubStatus = row.original.statusOption === 'is_sub_status';
        
        if (!isSubStatus) return '-';
        
        // Get available status fields
        const statusFields = tableData.filter(f => 
          f.statusOption === 'is_status' && 
          f.key_type === 'DROPDOWN' &&
          f.item_id !== row.original.item_id
        );
        
        return (
          <Select
            size="small"
            fullWidth
            value={row.original.status_parent_key ?? ''}
            onChange={(e) => {
              const updates = { status_parent_key: e.target.value };
              const updatedData = tableData.map(item =>
                item.item_id === row.original.item_id ? { ...item, ...updates } : item
              );
              setTableData(updatedData);
              autoSaveChanges(row.original.item_id, updates);
            }}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select Parent</em>
            </MenuItem>
            {statusFields.map(f => (
              <MenuItem key={f.item_id} value={f.key_name}>
                {f.alias_name || f.key_name}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      accessorKey: 'is_hot_lead',
      header: 'Hot',
      size: 80,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.is_hot_lead ?? false}
          onChange={(e) => {
            const updates = { is_hot_lead: e.target.checked };
            const updatedData = tableData.map(item =>
              item.item_id === row.original.item_id ? { ...item, ...updates } : item
            );
            setTableData(updatedData);
            autoSaveChanges(row.original.item_id, updates);
          }}
        />
      ),
    },
    {
      accessorKey: 'isFilter',
      header: 'Filter',
      size: 80,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.isFilter ?? false}
          onChange={(e) => {
            const updates = { isFilter: e.target.checked };
            const updatedData = tableData.map(item =>
              item.item_id === row.original.item_id ? { ...item, ...updates } : item
            );
            setTableData(updatedData);
            autoSaveChanges(row.original.item_id, updates);
          }}
        />
      ),
    },
    {
      accessorKey: 'isGroup',
      header: 'Group',
      size: 80,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.isGroup ?? false}
          onChange={(e) => {
            const updates = { isGroup: e.target.checked };
            const updatedData = tableData.map(item =>
              item.item_id === row.original.item_id ? { ...item, ...updates } : item
            );
            setTableData(updatedData);
            autoSaveChanges(row.original.item_id, updates);
          }}
        />
      ),
    },
    {
      accessorKey: 'isSuggestion',
      header: 'Suggestion',
      size: 100,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.isSuggestion ?? false}
          onChange={(e) => {
            const updates = { isSuggestion: e.target.checked };
            const updatedData = tableData.map(item =>
              item.item_id === row.original.item_id ? { ...item, ...updates } : item
            );
            setTableData(updatedData);
            autoSaveChanges(row.original.item_id, updates);
          }}
        />
      ),
    },
  ], [editingRowId, editedData, tableData]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          Tabular Form View
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            size="small"
          >
            Export CSV
          </Button>
        </Stack>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={tableData}
        enableRowNumbers={false}
        enableDensityToggle={false}
        enableFullScreenToggle={true}
        enableColumnActions={false}
        enableColumnFilters={true}
        enableHiding={true}
        enableStickyHeader={true}
        enableTopToolbar={true}
        enableBottomToolbar={true}
        enablePagination={false}
        enableRowSelection={false}
        enableRowOrdering={true}
        enableSorting={true}
        initialState={{ 
          density: 'compact',
          columnPinning: { left: ['drag'], right: ['mrt-row-actions'] }
        }}
        muiTableContainerProps={{
          sx: { maxHeight: 'calc(100vh - 300px)' }
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: 'primary.main',
            color: '#ffffff',
            fontWeight: '500',
            fontSize: '13px',
          }
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '12px',
          }
        }}
        muiTableBodyRowProps={({ row }) => ({
          onDragStart: () => setDraggedRowId(row.original.item_id),
          onDragEnd: () => setDraggedRowId(null),
          onDragOver: (e) => e.preventDefault(),
          onDrop: () => {
            if (draggedRowId === null || draggedRowId === row.original.item_id) return;
            
            const draggedIndex = tableData.findIndex(item => item.item_id === draggedRowId);
            const targetIndex = tableData.findIndex(item => item.item_id === row.original.item_id);
            
            const newData = [...tableData];
            const [draggedItem] = newData.splice(draggedIndex, 1);
            newData.splice(targetIndex, 0, draggedItem);
            
            const reorderedData = newData.map((item, index) => ({
              ...item,
              order_id: index + 1
            }));
            
            setTableData(reorderedData);
            if (setLeadsFormItems) setLeadsFormItems(reorderedData);
            setDraggedRowId(null);
          },
          draggable: true,
          sx: {
            cursor: 'move',
            opacity: draggedRowId === row.original.item_id ? 0.5 : 1,
          }
        })}
        enableRowActions={true}
        positionActionsColumn="last"
        renderTopToolbarCustomActions={() => (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            size="small"
            disabled={isAddingField}
          >
            Add Field
          </Button>
        )}
        renderRowActions={({ row }) => {
          const isStatic = row.original.is_static_column;
          
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!row.original.is_primary && !isStatic && (
                <>
                  <Tooltip title="Clone" arrow>
                    <IconButton 
                      size="small" 
                      color="info"
                      onClick={() => handleCloneRow(row)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteRow(row)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {(row.original.is_primary || isStatic) && (
                <Tooltip title={row.original.is_primary ? "Primary key cannot be deleted" : "Static column"} arrow>
                  <span>
                    <IconButton 
                      size="small" 
                      disabled
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        }}
      />

      {/* Options Modal */}
      <Dialog 
        open={showOptionsModal} 
        onClose={handleCancelOptions}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Manage Options for "{editingOptionsForRow?.alias_name || editingOptionsForRow?.key_name}"
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="New Option"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
                placeholder="Enter option and press Add"
              />
              <Button
                variant="contained"
                onClick={handleAddOption}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Stack>
          </Box>
          
          {currentOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No options added yet. Add your first option above.
            </Typography>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {currentOptions.map((option, index) => (
                <ListItem 
                  key={index}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemText 
                    primary={option}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      size="small"
                      color="error"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Total Options: {currentOptions.length}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOptions} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveOptions} 
            variant="contained"
            disabled={currentOptions.length === 0}
          >
            Save Options
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabularFormView;